import { useContext, useMemo } from 'react';

import { CalendarDayIcon, Label, Tooltip } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column, Row } from 'react-table';

import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { TDiscount } from 'types';
import { secondsToWeekDays } from 'utils';

import s from './Discounts.module.scss';
export const UseGetColumns = (): Column<TDiscount>[] => {
  const size = useWindowResize();
  const { t, lang } = useContext(LocalizationContext);
  const dayArr = useMemo(() => {
    const returnedDayName: { title: string; value: number }[] = [];

    for (let day = 1; day < 8; day++) {
      const weekday = dayjs().locale(lang).day(day).format('dd');

      returnedDayName.push({
        title: weekday.charAt(0).toUpperCase() + weekday.slice(1),
        value: day,
      });
    }

    return returnedDayName;
  }, [lang]);

  return useMemo(
    () => [
      {
        Header: t('state'),
        accessor: 'active',
        Cell: ({ value }) => {
          return value ? (
            <Label text={t('discounts.table.status')} color="green" />
          ) : (
            <Label
              text={t('discounts.table.status.off')}
              color="gray-primary"
            />
          );
        },
        maxWidth: 160,
        disableResizing: true,
      },
      {
        Header: t('title'),
        accessor: 'title',
        width: size.width > 960 ? 315 : 200,
        disableResizing: true,
      },
      {
        Header: t('tariff.schedule'),
        accessor: 'schedules',
        Cell: ({
          value,
          row,
        }: {
          value: { start: number; end: number }[];
          row: Row<TDiscount>;
        }) => {
          if (!value?.length) {
            return <span>—</span>;
          }

          const sorted = [...value].sort((a, b) => a.start - b.start);

          const days = sorted
            .map((p) => secondsToWeekDays(p.start))
            .map(
              (dayValue) => dayArr.find((d) => d.value === dayValue.day)?.title
            )
            .filter(Boolean)
            .join(',');

          const timeStart = secondsToWeekDays(value[0].start).hour;
          const timeEnd = secondsToWeekDays(value[0].end).hour;

          return (
            <div className={s.periods}>
              <div className={s.day}>
                <span className={s.days}>{days}</span>
                <span className={s.time}>
                  {timeStart}-{timeEnd}
                </span>
              </div>
              {row?.original?.dont_use_on_holidays && (
                <Tooltip content={t('discounts.table.holiday.off')}>
                  <div className={s.holiday}>
                    <CalendarDayIcon color="error" />
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
        width: 200,
      },
      {
        Header: t('value'),
        accessor: 'value',
        Cell: ({ value }) => <span>{value}%</span>,
        width: 80,
      },
    ],
    [t]
  );
};
